<template>
    <div class="card">
        <div class="card-logo">
            <div class="flex">
                <img :src="theme.loginLogo" alt="" class="card-logo-img" />
                <img :src="theme.loginText" alt="" class="card-logo-text" />
            </div>
            <div class="card-logo-msg" v-if="showMsg">
                {{ showMsg }}
            </div>
        </div>
        <div class="card-line"></div>
        <div class="card-content">
            <div class="card--header">
                {{ showTitle }}
            </div>
            <div class="card--main">
                <zh-tabs v-if="showTabStatus" v-model:active-name="systemLoginType" :tab-arr="tabSystemArr"></zh-tabs>
                <div class="form">
                    <div class="login-options" v-if="showTabStatus">
                        <div
                            class="login-option-item"
                            :class="{ active: loginType === LOGIN_TYPE.Account }"
                            @click="loginType = LOGIN_TYPE.Account"
                        >
                            <div>账号登录</div>
                            <div class="mark"></div>
                        </div>
                        <div
                            class="login-option-item"
                            :class="{ active: loginType === LOGIN_TYPE.Captcha }"
                            @click="loginType = LOGIN_TYPE.Captcha"
                        >
                            <div>验证码登录</div>
                            <div class="mark"></div>
                        </div>
                    </div>
                    <!--账号密码登录-->
                    <password-login
                        v-if="loginType === LOGIN_TYPE.Account"
                        @get-token="setToken"
                        @verify-identity="openVerifyIdentity"
                        :device-code="deviceCode"
                        :systemType="systemLoginType"
                        :isChecked="isChecked"
                        @isSelectAgreements="isSelectAgreements"
                    />
                    <!-- 验证码登录  -->
                    <code-login
                        ref="codeLoginRef"
                        :systemType="systemLoginType"
                        v-else-if="loginType === LOGIN_TYPE.Captcha"
                        @get-token="setToken"
                        @verify-identity="openVerifyIdentity"
                        :device-code="deviceCode"
                        :isChecked="isChecked"
                        @isSelectAgreements="isSelectAgreements"
                    />
                    <!--设置密码 -->
                    <reset-password
                        :systemType="systemLoginType"
                        v-else-if="loginType === LOGIN_TYPE.ResetPassword"
                        @cancel="loginType = LOGIN_TYPE.Account"
                    />
                    <!-- 验证身份 -->
                    <verify-identity
                        ref="verifyIdentityRef"
                        @get-token="setToken"
                        :systemType="systemLoginType"
                        v-else-if="loginType === LOGIN_TYPE.VerifyIdentity"
                        @cancel="loginType = LOGIN_TYPE.Account"
                        @reset-password="loginType = LOGIN_TYPE.ResetPassword"
                        :account="mobile"
                    />
                    <!-- 注册账号 -->
                    <register-account
                        :systemType="systemLoginType"
                        :isChecked="isChecked"
                        @isSelectAgreements="isSelectAgreements"
                        v-else-if="loginType === LOGIN_TYPE.RegisterAccount"
                        @cancel="loginType = LOGIN_TYPE.Account"
                    >
                    </register-account>
                    <!-- 忘记密码-->
                    <find-password
                        :systemType="systemLoginType"
                        :isChecked="isChecked"
                        @isSelectAgreements="isSelectAgreements"
                        v-else-if="loginType === LOGIN_TYPE.ForgetPassword"
                        @cancel="loginType = LOGIN_TYPE.Account"
                    >
                    </find-password>
                    <!--隐私与协议-->
                    <div
                        class="card--main--footer"
                        :class="{
                            'mt-50':
                                systemLoginType === SYSTEM_LOGIN_TYPE.ADMIN &&
                                (loginType === LOGIN_TYPE.Account || loginType === LOGIN_TYPE.Captcha)
                        }"
                        @click="changeCheckBox"
                    >
                        <el-radio-group v-model="isChecked" @change="changeCheckBox">
                            <el-radio :label="true"></el-radio>
                        </el-radio-group>
                        <div class="card--main--footer--msg">
                            已阅读并同意《<span @click.stop.prevent="onClickGoArticle(ARTICLE_TYPE.USER_AGREEMENT.code)"
                                >用户协议</span
                            >》和《<span @click.stop.prevent="onClickGoArticle(ARTICLE_TYPE.PRIVACY_AGREEMENT.code)"
                                >隐私政策</span
                            >》，允许{{ theme.systemAbbreviation }}管理本人账号信息
                            <div class="error-msg" v-if="showErrorMsg">请勾选协议</div>
                        </div>
                    </div>
                    <!-- 登录注册 -->
                    <div class="btn-box" v-if="showTabStatus && systemLoginType === SYSTEM_LOGIN_TYPE.CUSTOMER">
                        <div class="register-account btn" @click="loginType = LOGIN_TYPE.RegisterAccount">注册账号</div>
                        <div class="btn-box-line"></div>
                        <div class="register-account btn" @click="loginType = LOGIN_TYPE.ForgetPassword">忘记密码</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <UserReadDialog
        :systemType="systemLoginType"
        v-if="dialogVisible"
        v-model:visible="dialogVisible"
        :type="dialogType"
    ></UserReadDialog>
</template>

<script setup lang="ts">
import { ElRadio, ElRadioGroup } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { theme } from "@/config/sassTheme";
import { useUserStore } from "@/stores/modules/user";
import { getUserInfo } from "@/utils/login";
import ZhTabs from "@/views/components/zhTabs/index.vue";
import PasswordLogin from "./components/passwordLogin.vue";
import ResetPassword from "./components/resetPassword.vue";
import VerifyIdentity from "./components/verifyIdentity.vue";
import CodeLogin from "./components/codeLogin.vue";
import FindPassword from "./components/findPassword/index.vue";
import RegisterAccount from "./components/registerAccount/index.vue";
import { ARTICLE_TYPE, YES_OR_NO } from "@supplier/meta";
import UserReadDialog from "./components/userReadDialog/index.vue";
import { getDeviceCode } from "@/utils/deviceCode";
import { SYSTEM_LOGIN_TYPE, LOGIN_TYPE } from "./type.ts";

const codeLoginRef = ref<InstanceType<typeof CodeLogin>>();
const verifyIdentityRef = ref();

/** 关闭验证码 */
const onClickClostCaptcha = () => {
    codeLoginRef.value?.closeCaptchaWrap();
    verifyIdentityRef.value?.closeCaptchaWrap();
};
const showTitle = computed(() => {
    switch (loginType.value) {
        case LOGIN_TYPE.VerifyIdentity:
            return "验证身份";
        case LOGIN_TYPE.ResetPassword:
            return "设置密码";
        case LOGIN_TYPE.ForgetPassword:
            return "设置新密码";
        case LOGIN_TYPE.RegisterAccount:
            return "注册账号";
    }
    return "登录";
});

const showMsg = computed(() => {
    // if ([LOGIN_TYPE.ResetPassword, LOGIN_TYPE.VerifyIdentity].includes(loginType.value)) {
    //     return "供应链端";
    // } else if ([LOGIN_TYPE.ForgetPassword, LOGIN_TYPE.RegisterAccount].includes(loginType.value)) {
    //     return "客户端";
    // }
    return tabSystemArr[systemLoginType.value - 1]?.label || "";
});
const showTabStatus = computed(() => {
    return [LOGIN_TYPE.Account, LOGIN_TYPE.Captcha].includes(loginType.value);
});

const userStore = useUserStore();
/** 登录方式 */
const systemLoginType = ref(SYSTEM_LOGIN_TYPE.ADMIN);

const tabSystemArr = [
    {
        label: "供应链端",
        value: SYSTEM_LOGIN_TYPE.ADMIN
    },
    {
        label: "客户端",
        value: SYSTEM_LOGIN_TYPE.CUSTOMER
    }
];

/** 登录类型 */
const loginType = ref(LOGIN_TYPE.Account);
/**
 * 同意协议
 */
const isChecked = ref(false);

const setToken = async (token: string) => {
    if (systemLoginType.value === SYSTEM_LOGIN_TYPE.ADMIN) {
        userStore.setToken(token);
        await getUserInfo();
        if (userStore.userInfo.user.isSelfSetPassword === YES_OR_NO.NO.code) {
            loginType.value = LOGIN_TYPE.ResetPassword;
        } else {
            const jumpUrl = import.meta.env.VITE_DOMAIN_NAME as string;
            userStore.clear();
            window.location.href = jumpUrl + `?token=${token}`;
        }
    } else if (systemLoginType.value === SYSTEM_LOGIN_TYPE.CUSTOMER) {
        /** 点击跳转客户端登录页 */
        const jumpUrl = import.meta.env.VITE_JUMP_CONSOLE_URL as string;
        userStore.clear();
        window.location.href = jumpUrl + `?token=${token}`;
    }
};
const deviceCode = ref("");

const mobile = ref("");
// 新设备登录需要校验验证码
const openVerifyIdentity = (val: string) => {
    mobile.value = String(val);
    loginType.value = LOGIN_TYPE.VerifyIdentity;
};
onMounted(() => {
    setTimeout(async () => {
        // 获取设备UUID
        await getDeviceCode((code: string) => {
            deviceCode.value = code;
        });
    }, 100);
});
const showErrorMsg = ref(false);
const isSelectAgreements = () => {
    showErrorMsg.value = !isChecked.value;
};
const changeCheckBox = () => {
    isChecked.value = !isChecked.value;
    showErrorMsg.value = false;
};
//隐私协议弹窗类型
const dialogType = ref<number>(0);
//隐私协议弹窗是否显示
const dialogVisible = ref<boolean>(false);
const onClickGoArticle = (type: number) => {
    dialogType.value = type as number;
    dialogVisible.value = true;
};
defineExpose({
    onClickClostCaptcha
});
</script>

<style scoped lang="scss">
$primary-color: #1c5bff;
.card-logo-msg {
    display: inline-flex;
    padding: 6px 8px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 6px;
    background: #e8efff;
    color: $primary-color;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 12px;
    margin-top: 12px;
}
:deep(.login-form) {
    :deep(.el-input) {
        --el-border-radius-base: 6px;
        .el-input__prefix-inner > :last-child {
            margin-right: 12px;
        }
    }
}
.card {
    width: 766px;
    height: 448px;
    box-sizing: border-box;
    background: #ffffff;
    border-radius: 20px;
    left: 118px;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    padding: 0;
    overflow: hidden;
    &-logo {
        width: 312px;
        height: 100%;
        background: url("#{$oss-url}/zhongsheng-admin/login-mask.png") no-repeat center center;
        background-size: 100% 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        &-img {
            width: 60px;
            margin-right: 8px;
        }
        &-text {
            width: 150px;
        }
    }
    &-line {
        width: 1px;
        height: 396px;
        background: #0000000f;
        margin-top: 26px;
    }
    &-content {
        flex: 1;
        position: relative;
    }
    .login-options {
        display: flex;
        width: 100%;
        cursor: pointer;
        user-select: none;
        gap: 35px;
        margin-bottom: 21px;
        color: #000000a6;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 18px;
        .active {
            position: relative;
            color: #000000e0;
            font-size: 18px;
            font-style: normal;
            font-weight: 500;
            .mark {
                position: absolute;
                left: -1px;
                bottom: -6px;
                flex-shrink: 0;
                width: 67px;
                height: 10px;
                opacity: 0.5;
                background: linear-gradient(90deg, var(--el-color-theme) 0%, var(--el-color-theme-light2) 100%);
            }
        }
    }
    &-close {
        position: absolute;
        right: -32px;
        top: 0;
        font-size: 22px;
        cursor: pointer;
        color: #ffffff;
    }
    &--header {
        color: #000000e0;
        font-size: 22px;
        font-style: normal;
        font-weight: 600;
        line-height: 22px;
        text-align: center;
        padding-top: 26px;
    }
    .form {
        padding-top: 20px;
    }
    &--main {
        padding: 0 35px;
        margin-top: 20px;
        &--tips {
            display: flex;
            justify-content: start;
            margin-top: 30px;
            margin-bottom: 30px;

            .tab {
                cursor: pointer;
                line-height: 18px;
            }

            .account {
                width: 100px;
            }

            .active {
                color: #000000;
                font-size: 18px;
            }

            &--title {
                color: #000000a6;
                font-size: 14px;
                font-weight: 400;
            }
        }
        &--input {
            position: relative;
            .captcha-btn {
                position: absolute;
                right: 0;
                top: -12px;
                .code-btn {
                    font-weight: normal;
                }
                .wait {
                    text-align: center;
                }
            }

            .captcha-msg {
                color: var(--el-color-danger);
                font-size: 12px;
                position: absolute;
                top: 25px;
            }

            :deep .el-input {
                margin-bottom: 20px;

                .el-input__wrapper {
                    border-bottom: 2px solid #e4e6ea;
                    box-shadow: none !important;
                    padding: 0;
                    border-radius: 0;

                    .el-input__inner {
                        padding-bottom: 9px;
                        color: #999999;
                        font-size: 14px;
                        font-weight: 400;
                    }
                }

                & .is-focus {
                    border-bottom: 2px solid var(--el-color-theme);
                }
            }

            .wait {
                color: var(--el-color-button-text-color);
                background: var(--el-color-theme);
                width: 102px;
                border-radius: 5px;
                cursor: pointer;
            }

            :deep .el-form {
                .el-form-item {
                    margin-bottom: 10px;
                }

                .el-form-item__error {
                    top: 34px;
                }
            }
        }
        &--tip {
            color: #999999;
            font-size: 12px;
            font-weight: 400;
            margin-top: -5px;
        }
        &--footer {
            display: inline-flex;
            align-items: baseline;
            margin-top: 10px;
            position: relative;
            :deep .el-radio {
                height: fit-content;
                margin-right: 8px;
                position: absolute;
                top: 4px;
                .el-radio__inner {
                    width: 14px;
                    height: 14px;
                    background-color: #ffffff !important;
                    &::after {
                        width: 4px !important;
                        height: 4px !important;
                        background-color: $primary-color !important;
                    }
                }
                .el-radio__label {
                    display: none;
                }
            }
            &--msg {
                padding-left: 28px;
                cursor: pointer;
                color: #00000066;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                span {
                    color: $primary-color;
                    font-size: 12px;
                    font-style: normal;
                    font-weight: 400;
                    cursor: pointer;
                }
                .error-msg {
                    color: var(--el-color-danger);
                    font-size: 12px;
                }
            }
        }
        .btn-box {
            display: flex;
            justify-content: center;
            align-items: center;
            padding-top: 20px;
            &-line {
                width: 1px;
                height: 14px;
                background: #dbdbdb;
                margin: 0 40px;
            }
            .register-account {
                color: var(--el-color-theme) !important;
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: 14px;
            }
        }
        .btn {
            cursor: pointer;
            text-align: center;
            color: #000000a6;
            font-size: 12px;
            font-weight: 400;
        }
        //&--qrcode {
        //    display: flex;
        //    align-items: center;
        //    flex-direction: column;
        //
        //    #qrcode-container {
        //        margin-top: 25px;
        //
        //        :deep(iframe) {
        //            height: 230px;
        //        }
        //    }
        //
        //    &--tip {
        //        margin-top: 25px;
        //        color: #333333;
        //        font-size: 16px;
        //        font-style: normal;
        //        font-weight: bold;
        //        line-height: 16px;
        //        text-transform: capitalize;
        //        display: flex;
        //        align-items: center;
        //        justify-content: center;
        //
        //        .iconfont {
        //            font-size: 18px;
        //            font-weight: normal;
        //            color: #4cbf00;
        //            margin-right: 8px;
        //        }
        //    }
        //}
    }

    .captcha-wrap {
        position: absolute;
        width: 100%;
        top: 188px;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
    }
}
</style>
